import React, { FC, useState, useEffect, useRef } from 'react'
import Select from 'react-select'
import style from '../style.scss'
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    Label
} from 'recharts'

interface Props {
    type: string
    heading: string
    dataKey: string
    agentsStatsData: any
    performanceMatricsList: any
    defaultColor?: string
    backgroundColor?: string
    botNames?: any
}

const PerformanceGraph: FC<Props> = props => {
    const [selectedPerformance, setSelectedPerformance] = useState({ label: 'Total requests', value: 'totalRequests' })
    const yLabelRef = useRef('Count')
    const [performance, setPerformance]: any = useState([])
    const performanceMatricsList = props.performanceMatricsList
    const defaultColor = props.defaultColor
    const backgroundColor = props?.backgroundColor

    useEffect(() => {
        // Bot performance Matrics
        const processData = () => {
            if (props.type === 'botPerformance') {
                const botPerformance = props.agentsStatsData?.filter(botData => botData.botId).reduce((accumulator, item) => {
                    const key = `${item.botId}`
                    const botName = props.botNames[key] ? props.botNames[key] : 'N/A'
                    if(props.botNames[key]) {
                        if (!accumulator[key]) {
                            accumulator[key] = {
                                id: item.id,
                                agentId: item.agentId,
                                email: item.email,
                                botId: item.botId,
                                botName,
                                resolvedRequests: 0,
                                linkedAgent: item.linkedAgent,
                                [selectedPerformance.value]: 0
                            }
                        }
                        accumulator[key][selectedPerformance.value] += item[selectedPerformance.value]
                        if (selectedPerformance.value === 'resolveTime') {
                            accumulator[key]['resolvedRequests'] += item['resolvedRequests']
                        }
                    }
                    return accumulator
                }, {})

                // Convert botPerformance object to an array
                const botPerformanceValues: any = Object.values(botPerformance)
                const modifiedResolveTime = botPerformanceValues.map((botPerformanceValue) => {
                    const avgResolveTime = botPerformanceValue['resolvedRequests'] > 0 ? (botPerformanceValue['resolveTime'] / botPerformanceValue['resolvedRequests']) / 60 : 0
                    return {
                        ...botPerformanceValue,
                        resolveTime: avgResolveTime.toFixed(1)
                    }
                })
                setPerformance(modifiedResolveTime)
            } else if (props.type === 'agentPerformance') {
                const agentPerformance = props.agentsStatsData?.reduce((accumulator, item) => {
                    const key = `${item.agentId}_${item.email}`
                    if (!accumulator[key]) {
                        accumulator[key] = {
                            id: item.id,
                            agentId: item.agentId,
                            email: item.email,
                            botId: item.botId,
                            resolvedRequests: 0,
                            [selectedPerformance.value]: 0
                        }
                    }
                    accumulator[key][selectedPerformance.value] += item[selectedPerformance.value]
                    if (selectedPerformance.value === 'resolveTime') {
                        accumulator[key]['resolvedRequests'] += item['resolvedRequests']
                    }
                    return accumulator
                }, {})

                const agentPerformanceValues: any = Object.values(agentPerformance)
                const modifiedResolveTime = agentPerformanceValues.map((agentPerformanceValue) => {
                    const avgResolveTime = agentPerformanceValue['resolvedRequests'] > 0 ? (agentPerformanceValue['resolveTime'] / agentPerformanceValue['resolvedRequests']) / 60 : 0
                    return {
                        ...agentPerformanceValue,
                        resolveTime: avgResolveTime.toFixed(1)
                    }
                })
                setPerformance(modifiedResolveTime)
            }
            if (selectedPerformance.value !== 'resolveTime') {
                yLabelRef.current = 'Count'
            } else {
                yLabelRef.current = 'Time (in mins.)'
            }
        }
        void processData()
    }, [selectedPerformance, props.agentsStatsData, props.botNames])


    function handlePerformanceChange(event) {
        setSelectedPerformance({ label: event.label, value: event.value })
    }

    const renderLegend = (props) => {
        const { payload } = props
        return (
            <ul className={style.legendItems}>
                {
                    payload.map((entry, index) => (
                        <li className={style.legendItem} key={`item-${index}`}><div className={style.legendNode} style={{ background: defaultColor }}></div>{selectedPerformance.value}</li>
                    ))
                }
            </ul>
        )
    }

    return <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{props.heading}</h2>
            <div style={{ width: '220px' }}>
                <Select
                    classNamePrefix={'performance-graphs'}
                    id="agents"
                    default={performanceMatricsList[0]}
                    maxMenuHeight={200}
                    style={{ zIndex: 1000 }}
                    options={performanceMatricsList}
                    value={selectedPerformance}
                    onChange={handlePerformanceChange}
                    styles={{
                        container: (styles) => ({
                            ...styles,
                            width: '100%',
                            fontSize: '12px',
                        }),
                        control: (styles) => ({
                            ...styles,
                            minHeight: '25px',
                            height: '25px',
                        }),
                        dropdownIndicator: (styles) => ({
                            ...styles,
                            padding: '0px 4px'
                        })
                    }}
                />
            </div>
        </div>
        <ResponsiveContainer height={250}>
            <BarChart
                width={500}
                height={400}
                data={performance}
                margin={{
                    top: 30,
                    right: 20,
                    left: 20,
                    bottom: 5,
                }}
                barSize={20}
            >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey={props.dataKey} />
                <YAxis label={{ value: yLabelRef.current, angle: -90, position: 'insideLeft' }} />
                <Legend wrapperStyle={{ position: 'absolute', bottom: '-10px' }} content={renderLegend} />
                <Tooltip cursor={{ fill: 'transparent' }} content={({ active, payload, label }: any) => {
                    if (active && payload && payload.length) {
                        let labelInfo = payload[0].payload
                        const linkedAgent = labelInfo.linkedAgent
                        if (linkedAgent === false || linkedAgent === 0) {
                            label = label + ' (Not Linked)'
                        }
                        return (
                            <div className={style.customToolTip}>
                                <p className={style.maiLabel}>{label}</p>
                                {<p className={style.label} style={{ color: defaultColor }}>{`${selectedPerformance.value} : ${labelInfo[selectedPerformance.value]}`}</p>}
                            </div>
                        )
                    }
                }} />
                <Bar dataKey={selectedPerformance.value} fill={props?.defaultColor} background={{ fill: backgroundColor }}
                />
            </BarChart>
        </ResponsiveContainer>
    </div>
}

export default PerformanceGraph
